import { useState } from 'react'
import { Modal, Button, Form, Divider } from 'semantic-ui-react'
import EmailField from './EmailField'
import api from 'api/index'
import BranchRole from 'models/BranchRole'
import Branch from 'interfaces/Branch'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'
import User from 'interfaces/User'
import BranchSection from './BranchSection'
import IBranchRole from 'interfaces/BranchRole'

interface FormState {
    email: string,
    branchRoles: IBranchRole[]
}

interface PropTypes {
    user: User,
    branches: Branch[],
    onSuccess: (updatedUser: User) => void
}

function EditUserModal(props: PropTypes) {
    const [formState, setFormState] = useState<FormState>({ email: props.user.email,
        branchRoles: props.user.branchRoles })
    const [isOpen, setIsOpen] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        try {
            e.preventDefault()
            setIsSubmitting(true)

            const response = await api.admin.editUserBranchRoles(
                props.user.userId, formState.branchRoles)

            setIsOpen(false)
            props.onSuccess({ ...props.user, branchRoles: response.data.branchRoles })
            toast.success(`Successfully edited user with email ${formState.email}.`)
        }
        catch (e) {
            if (e instanceof AxiosError) {
                const erroMessage = e.response?.data.error?.message ?? e.message
                toast.error(`Failed to edit user: ${erroMessage}.`)
            }
        }
        finally {
            setIsSubmitting(false)
        }
    }

    function isStateValid() {
        if (formState.branchRoles.some(branchRole =>
            branchRole.branchId < 0 || branchRole.role === BranchRole.None)) {
            return false
        }

        return true
    }

    return (
        <Modal
            as={Form}
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            onSubmit={handleSubmit}
            trigger={<Button basic circular size='mini' icon='edit' title='Edit user.'/>}
            size='small'
            dimmer='inverted'
        >
            <Modal.Header>Edit User</Modal.Header>
            <Modal.Content>
                <EmailField value={formState.email} disabled
                    onChange={email => setFormState({ ...formState, email })} />
                <Divider hidden />
                {<BranchSection value={props.user.branchRoles} branches={props.branches}
                    onChange={branchRoles => { setFormState({ ...formState, branchRoles })} }
                />}
            </Modal.Content>
            <Modal.Actions>
                <Button disabled={isSubmitting} content='Cancel'
                    type='button' onClick={() => setIsOpen(false)}/>
                <Button disabled={isSubmitting || !isStateValid()} loading={isSubmitting}
                    type='submit' content='Ok' primary />
            </Modal.Actions>
        </Modal>
    )
}

export default EditUserModal
